<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import WhatsappFlowsAPI from 'dashboard/api/whatsappFlows';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextTextArea from 'dashboard/components-next/textarea/TextArea.vue';

// Mensagem padrão que acompanha o Flow ao enviá-lo na conversa. Só local (não altera o Flow
// na Meta), vale também para Flows publicados; no envio os textos continuam editáveis.
const emit = defineEmits(['saved']);

const LIMITS = { button_text: 20, header: 60, body: 1024, footer: 60 };

const { t } = useI18n();
const dialogRef = ref(null);
const flow = ref(null);
const isSaving = ref(false);
const form = reactive({ button_text: '', header: '', body: '', footer: '' });

const tooLong = key => form[key].trim().length > LIMITS[key];
const hasErrors = computed(() => Object.keys(LIMITS).some(tooLong));

const open = target => {
  flow.value = target;
  const defaults = target?.send_defaults || {};
  Object.keys(LIMITS).forEach(key => {
    form[key] = defaults[key] || '';
  });
  dialogRef.value?.open();
};

const save = async () => {
  if (!flow.value || hasErrors.value || isSaving.value) return;
  isSaving.value = true;
  try {
    const sendDefaults = Object.fromEntries(
      Object.keys(LIMITS).map(key => [key, form[key].trim()])
    );
    const { data } = await WhatsappFlowsAPI.updateSendDefaults(
      flow.value.id,
      sendDefaults
    );
    emit('saved', data);
    useAlert(t('WHATSAPP_FLOWS.SEND_DEFAULTS.SAVED'));
    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('WHATSAPP_FLOWS.SEND_DEFAULTS.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    solid
    width="3xl"
    overflow-y-auto
    sticky-header-footer
    :title="t('WHATSAPP_FLOWS.SEND_DEFAULTS.TITLE')"
    :description="
      t('WHATSAPP_FLOWS.SEND_DEFAULTS.DESCRIPTION', { name: flow?.name || '' })
    "
    :confirm-button-label="t('WHATSAPP_FLOWS.SEND_DEFAULTS.SAVE')"
    :is-loading="isSaving"
    :disable-confirm-button="hasErrors || isSaving"
    @confirm="save"
  >
    <div class="grid gap-5 md:grid-cols-[minmax(0,1fr)_16rem]">
      <div class="flex min-w-0 flex-col gap-4">
        <div class="grid gap-4 sm:grid-cols-2">
          <NextInput
            v-model="form.button_text"
            :label="t('WHATSAPP_FLOWS.SEND.CTA')"
            :placeholder="t('WHATSAPP_FLOWS.SEND.CTA_PLACEHOLDER')"
            :maxlength="String(LIMITS.button_text)"
            :message="t('WHATSAPP_FLOWS.SEND_DEFAULTS.CTA_HINT')"
            :message-type="tooLong('button_text') ? 'error' : 'info'"
            data-test="send-default-cta"
          />
          <NextInput
            v-model="form.header"
            :label="t('WHATSAPP_FLOWS.SEND.HEADER')"
            :maxlength="String(LIMITS.header)"
            :message-type="tooLong('header') ? 'error' : 'info'"
          />
        </div>
        <NextTextArea
          v-model="form.body"
          :label="t('WHATSAPP_FLOWS.SEND.BODY')"
          :placeholder="t('WHATSAPP_FLOWS.SEND.BODY_PLACEHOLDER')"
          :max-length="LIMITS.body"
          show-character-count
          resize
        />
        <NextInput
          v-model="form.footer"
          :label="t('WHATSAPP_FLOWS.SEND.FOOTER')"
          :maxlength="String(LIMITS.footer)"
          :message-type="tooLong('footer') ? 'error' : 'info'"
        />
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('WHATSAPP_FLOWS.SEND_DEFAULTS.HELP') }}
        </p>
      </div>

      <aside class="flex flex-col gap-2">
        <p
          class="mb-0 text-xs font-semibold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('WHATSAPP_FLOWS.SEND.PREVIEW') }}
        </p>
        <div class="rounded-2xl bg-n-alpha-2 p-3">
          <div
            class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-sm"
          >
            <div class="flex flex-col gap-1 px-3 py-2.5">
              <p
                v-if="form.header.trim()"
                class="mb-0 break-words text-sm font-semibold text-n-slate-12"
              >
                {{ form.header.trim() }}
              </p>
              <p
                class="mb-0 whitespace-pre-wrap break-words text-sm text-n-slate-12"
              >
                {{ form.body.trim() || t('WHATSAPP_FLOWS.SEND.BODY_PREVIEW') }}
              </p>
              <p
                v-if="form.footer.trim()"
                class="mb-0 break-words text-xs text-n-slate-11"
              >
                {{ form.footer.trim() }}
              </p>
            </div>
            <div
              class="flex items-center justify-center gap-1.5 border-t border-n-weak px-3 py-2 text-sm font-medium text-n-blue-11"
            >
              <span class="i-hugeicons-workflow-square-06 size-4" />
              <span class="truncate">
                {{
                  form.button_text.trim() ||
                  t('WHATSAPP_FLOWS.SEND.CTA_PREVIEW')
                }}
              </span>
            </div>
          </div>
        </div>
      </aside>
    </div>
  </Dialog>
</template>
